'use client';

import { useCallback, useRef } from 'react';
import useSWR from 'swr';
import { useUser } from '@clerk/clerk-react';

import { fetchPreferenceTopics, setPreferenceTopic } from '@/lib/courier-inbox';
import type { CourierPreferenceTopic } from '@/types/notifications';
import { useNotificationToken } from './useNotificationToken';

export interface NotificationPreferences {
  /** False until the environment proves notifications configured — hide the section. */
  enabled: boolean;
  topics: CourierPreferenceTopic[];
  isLoading: boolean;
  /** Optimistic per-topic toggle; throws on vendor failure AFTER rolling back only the
   * touched topic, so concurrent toggles on other topics survive. */
  setTopic: (topicId: string, optedIn: boolean) => Promise<void>;
}

export function useNotificationPreferences(): NotificationPreferences {
  const { user } = useUser();
  const userId = user?.id ?? null;
  const { enabled, jwt } = useNotificationToken();

  const topicsQuery = useSWR<CourierPreferenceTopic[]>(
    enabled && userId && jwt ? ['notification-preferences', userId] : null,
    () => fetchPreferenceTopics(userId!, jwt!),
    { revalidateOnFocus: false },
  );

  // Per-topic write chain: rapid toggles of the same topic run their PUTs in order, so
  // the vendor's final state always matches the last user intent — overlapping requests
  // completing out of order can never leave cache and vendor disagreeing.
  const writeChains = useRef(new Map<string, Promise<void>>());

  const setTopic = useCallback(
    async (topicId: string, optedIn: boolean) => {
      if (!userId || !jwt) return;
      const previousStatus = topicsQuery.data?.find((t) => t.topic_id === topicId)?.status;
      const apply = (status: CourierPreferenceTopic['status']) =>
        topicsQuery.mutate(
          (current) => current?.map((t) => (t.topic_id === topicId ? { ...t, status } : t)),
          { revalidate: false },
        );

      void apply(optedIn ? 'OPTED_IN' : 'OPTED_OUT');
      const chain = (writeChains.current.get(topicId) ?? Promise.resolve())
        // A failed predecessor must not wedge the chain — each write stands alone.
        .catch(() => undefined)
        .then(() => setPreferenceTopic(userId, jwt, topicId, optedIn));
      writeChains.current.set(topicId, chain);

      try {
        await chain;
      } catch (err) {
        if (previousStatus) void apply(previousStatus);
        throw err;
      }
    },
    [userId, jwt, topicsQuery],
  );

  return {
    enabled,
    topics: topicsQuery.data ?? [],
    isLoading: topicsQuery.isLoading,
    setTopic,
  };
}
